import { useLocation } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { AccentScope } from '@/components/accent'
import { restaurantNav } from '@/features/back-office/nav'
import { useRestaurantScope } from '@/features/back-office/scope'
import { BottomBar } from '@/features/nav/bottom-bar'
import { navFor } from '@/features/nav/items'
import { Sidebar } from '@/features/nav/sidebar'
import { TopBar } from '@/features/nav/top-bar'
import { type System, useAuth } from '@/lib/auth-store'
import { cx } from '@/lib/cx'
import { useT } from '@/lib/i18n/translate'
import { type NavTarget, activeNavItem, visibleNavItems } from '@/lib/nav'

/** Pages outside the nav that still name themselves in the tab title (2026-09-13). */
const TITLED_PAGES: Pick<NavTarget, 'to' | 'label' | 'params'>[] = [
  { to: '/app/notifications', label: 'notification.title' },
  { to: '/notifications', label: 'notification.title' },
]

/**
 * The logged-in chrome (T13). Customer ordering pages stay outside it (G6).
 * Inside a restaurant scope (E6) the whole shell takes that restaurant's
 * accent (D3) and its role-filtered destinations lead the system ones.
 */
export function AppShell<S extends System>({ system, children }: { system: S; children: ReactNode }) {
  const t = useT()
  const auth = useAuth(system)
  const scope = useRestaurantScope()
  const { pathname } = useLocation()
  // Inside a restaurant only its pages are in the nav; the account pages stay under the avatar menu.
  const items = scope ? restaurantNav(scope) : visibleNavItems(navFor(system), auth.status === 'authenticated' ? auth.user : undefined)
  // Admin and staff have no Branches item (2026-09-14) but may still land on the page.
  const titled = scope ? [...TITLED_PAGES, { to: '/$restaurantSlug/manage/branches', params: { restaurantSlug: scope.restaurantSlug }, label: 'nav.branches' } as const] : TITLED_PAGES
  const active = activeNavItem(pathname, [...items, ...titled])

  const shell = (
    <div className="flex min-h-dvh flex-col">
      <TopBar
        system={system}
        auth={auth}
        restaurant={scope?.context?.settings.name}
        branch={scope?.branch?.name}
        page={active ? t(active.label) : undefined}
      />
      <div className="flex flex-1">
        <Sidebar items={items} />
        {/* Bottom padding keeps content clear of the fixed bar on small screens. */}
        <div className={cx('min-w-0 flex-1', items.length > 0 && 'pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pb-0')}>{children}</div>
      </div>
      <BottomBar items={items} />
    </div>
  )
  return scope?.context ? <AccentScope color={scope.context.settings.color}>{shell}</AccentScope> : shell
}
